import { GretaIconVariant } from '@cineamo/ui/src/icons/ic-greta';

import { ISO_639_3_Locale } from '../../constants/locale-constants';
import { ImprintDto } from '../../types/ImprintDto.type';
import { CinemaDto } from '../cinema/CinemaDto.types';

import { FacebookConfiguration } from './FacebookConfiguration';
import { InfoBannerConfig } from './InfoBannerConfig.types';
import { ShopLink } from './ShopLink.types';
import { WebsiteHeroSectionTabItem } from './WebsiteHeroSectionTabItem.types';
import { WebsiteHomeSectionItem } from './WebsiteHomeSectionItem.types';
import { WebsiteNavigatonItem } from './WebsiteNavigatonItem.types';
import { WebsiteProgramDateFilter } from './WebsiteProgramDateFilter.enum';
import { WebsiteProgramListLayout } from './WebsiteProgramListLayout.enum';
import { WebsiteStyle } from './WebsiteStyle.types';
import { WebsiteTheme } from './WebsiteTheme.types';

export type ProgramSortingKey =
    | 'date-asc'
    | 'date-desc'
    | 'a-z'
    | 'z-a'
    | 'showings-asc'
    | 'showings-desc'
    | 'newest-asc'
    | 'newest-desc';

export const PROGRAM_SORTING_KEYS: ProgramSortingKey[] = [
    'date-asc',
    'date-desc',
    'a-z',
    'z-a',
    'showings-asc',
    'showings-desc',
    'newest-asc',
    'newest-desc'
];

/**
 * Sorting offered for the "in cinemas" tab of the home page hero slider. Deliberately narrower than
 * the program list's keys: only these orders make sense for a slider, and each has one sensible
 * direction — most popular first, newest first, A to Z — so no ascending/descending pairs.
 */
export type HeroInCinemasSortingKey = 'popularity' | 'release-date' | 'alphabetical' | 'date';

export const HERO_IN_CINEMAS_SORTING_KEYS: HeroInCinemasSortingKey[] = [
    'popularity',
    'release-date',
    'alphabetical',
    'date'
];

export type WebsitePageMetadataKey = 'home' | 'program' | 'specials' | 'announcements' | 'info' | 'deals';

export const WEBSITE_PAGE_METADATA_KEYS: WebsitePageMetadataKey[] = [
    'home',
    'program',
    'specials',
    'announcements',
    'info',
    'deals'
];

export type WebsitePageMetadata = {
    description?: string;
};

export type WebsiteMetadataConfiguration = Partial<Record<WebsitePageMetadataKey, WebsitePageMetadata>>;

export type WebsiteConfiguration = {
    isEyeAbleEnabled?: boolean;
    /**
     * Video linked to the cinema's profile image. When set, the profile image
     * on the website's info page shows a play button that opens this video.
     */
    profileVideoUrl?: string;
    /**
     * Meta Pixel ID (numeric string, typically 15-16 digits) used to load the
     * Meta Pixel base code (fbq init + PageView) on the cinema website. Unset
     * or empty disables the pixel. Only loaded once a visitor consents to
     * analysis cookies.
     */
    metaPixelId?: string;
    /**
     * Self-hosted Matomo instance used to track the cinema website in cookieless mode
     * (DoNotTrack + disableCookies, no consent banner integration needed). Both `trackerUrl`
     * (the Matomo instance's base URL, e.g. `https://piwik.t3m.de`) and `siteId` must be set for
     * tracking to be injected.
     */
    matomo?: {
        trackerUrl?: string;
        siteId?: string;
    };
    cineamoAppBanner?: {
        isEnabled?: boolean;
        text?: string;
    };
    infoBanner?: InfoBannerConfig;
    /** @deprecated Replaced with `shopLinks` (entry with `type: 'voucher'`). Kept for backward compatibility. */
    voucherUrl?: string;
    withdrawalUrl?: string;
    /** @deprecated Replaced with `shopLinks` (entry with `type: 'voucher'`). Kept for backward compatibility. */
    useEmbeddedShop?: boolean;
    shopLinks?: ShopLink[];
    isWhatsAppNewsletterActive?: boolean;
    facebookConfiguration?: FacebookConfiguration;
    /** @deprecated Replaced with `imprints` */
    imprint?: ImprintDto;
    imprints?: ImprintDto[];
    termsAndConditionsUrl?: string;
    style?: WebsiteStyle;
    /**
     * Optional dark-theme color overrides, applied field-wise on top of
     * `style` when the visitor uses the dark color theme. The dark theme
     * (and its switch on the website) is only available when this is set.
     */
    darkStyle?: Partial<WebsiteStyle>;
    theme?: WebsiteTheme;
    navigation?: WebsiteNavigatonItem[];
    homePageSections?: WebsiteHomeSectionItem[];
    homePageHeroSectionTabs?: WebsiteHeroSectionTabItem[];
    defaultProgramAllSorting?: ProgramSortingKey;
    defaultProgramDailySorting?: ProgramSortingKey;
    /** Sorting of the "in cinemas" tab in the home page hero slider. Defaults to popularity. */
    heroInCinemasSorting?: HeroInCinemasSortingKey;
    programListMobileLayout?: WebsiteProgramListLayout;
    /**
     * Layout of the program list on desktop. Independent of the mobile setting, so a cinema can run
     * the compact list on wide screens while keeping the default one on phones, or the reverse.
     * Defaults to the default layout, which is what desktop showed before this was configurable.
     */
    programListDesktopLayout?: WebsiteProgramListLayout;
    /**
     * Date tab preselected on the program page when the URL does not carry a
     * `date` query parameter (e.g. when navigating there from the home page).
     * Defaults to `today`.
     */
    initialProgramDateFilter?: WebsiteProgramDateFilter;
    forms?: {
        rentACinema?: {
            emailTo: string;
            serviceEmail?: string;
            servicePhone?: string;
            useUnstyledEmail?: boolean;
            /** Card headline on the home page. Falls back to the translated default when unset. */
            cardTitle?: string;
            /** Card body on the home page. Plain text, no formatting. */
            cardText?: string;
            /** Label of the card's button. Falls back to the translated default when unset. */
            cardButtonText?: string;
            /** Headline on the form's own page. Falls back to the translated default when unset. */
            pageTitle?: string;
            /**
             * Body on the form's own page, markdown. Replaces the page's default copy entirely, so
             * only text authored here counts as an override.
             */
            pageText?: string;
            /**
             * @deprecated Replaced with `cardText` (home page card) and `pageText` (the form's own
             * page). This single field fed both before they were split. No longer written — kept
             * because it is still read as the card text's fallback, so wording entered before the
             * split survives until the operator saves the form once.
             */
            description?: string;
            /** Ref returned by the `cinema-card-image` upload endpoint (reused for form images). */
            imageRef?: string;
            /**
             * Whether the form is offered at all: false hides its card on the home page and makes
             * its own page 404. The configuration is kept either way, so switching it back on
             * restores the page with its copy intact. Defaults to true when unset.
             */
            isActive?: boolean;
            /**
             * Whether the card is listed on the home page. Narrower than `isActive`: the form's own
             * page stays reachable through its link. Defaults to true when unset.
             */
            isVisible?: boolean;
        };
        kidsNTeens?: {
            emailTo: string;
            serviceEmail?: string;
            servicePhone?: string;
            useUnstyledEmail?: boolean;
            /** Card headline on the home page. Falls back to the translated default when unset. */
            cardTitle?: string;
            /** Card body on the home page. Plain text, no formatting. */
            cardText?: string;
            /** Label of the card's button. Falls back to the translated default when unset. */
            cardButtonText?: string;
            /** Headline on the form's own page. Falls back to the translated default when unset. */
            pageTitle?: string;
            /**
             * Body on the form's own page, markdown. Replaces the page's default copy entirely, so
             * only text authored here counts as an override.
             */
            pageText?: string;
            /**
             * @deprecated Replaced with `cardText` (home page card) and `pageText` (the form's own
             * page). This single field fed both before they were split. No longer written — kept
             * because it is still read as the card text's fallback, so wording entered before the
             * split survives until the operator saves the form once.
             */
            description?: string;
            imageRef?: string;
            /**
             * Whether the form is offered at all: false hides its card on the home page and makes
             * its own page 404. The configuration is kept either way, so switching it back on
             * restores the page with its copy intact. Defaults to true when unset.
             */
            isActive?: boolean;
            /**
             * Whether the card is listed on the home page. Narrower than `isActive`: the form's own
             * page stays reachable through its link. Defaults to true when unset.
             */
            isVisible?: boolean;
        };
        birthdayAtTheCinema?: {
            emailTo: string;
            serviceEmail?: string;
            servicePhone?: string;
            useUnstyledEmail?: boolean;
            /** Card headline on the home page. Falls back to the translated default when unset. */
            cardTitle?: string;
            /** Card body on the home page. Plain text, no formatting. */
            cardText?: string;
            /** Label of the card's button. Falls back to the translated default when unset. */
            cardButtonText?: string;
            /** Headline on the form's own page. Falls back to the translated default when unset. */
            pageTitle?: string;
            /**
             * Body on the form's own page, markdown. Replaces the page's default copy entirely, so
             * only text authored here counts as an override.
             */
            pageText?: string;
            /**
             * @deprecated Replaced with `cardText` (home page card) and `pageText` (the form's own
             * page). This single field fed both before they were split. No longer written — kept
             * because it is still read as the card text's fallback, so wording entered before the
             * split survives until the operator saves the form once.
             */
            description?: string;
            imageRef?: string;
            /**
             * Whether the form is offered at all: false hides its card on the home page and makes
             * its own page 404. The configuration is kept either way, so switching it back on
             * restores the page with its copy intact. Defaults to true when unset.
             */
            isActive?: boolean;
            /**
             * Whether the card is listed on the home page. Narrower than `isActive`: the form's own
             * page stays reachable through its link. Defaults to true when unset.
             */
            isVisible?: boolean;
        };
        giftCards?: {
            emailTo: string;
            serviceEmail?: string;
            servicePhone?: string;
            prices?: {
                giftVoucher?: number;
                concessionVoucher?: number;
                dboxVoucher?: number;
                goldenEnvelope?: number;
                processingFee?: number;
            };
            useUnstyledEmail?: boolean;
            /** Card headline on the home page. Falls back to the translated default when unset. */
            cardTitle?: string;
            /** Card body on the home page. Plain text, no formatting. */
            cardText?: string;
            /** Label of the card's button. Falls back to the translated default when unset. */
            cardButtonText?: string;
            /** Headline on the form's own page. Falls back to the translated default when unset. */
            pageTitle?: string;
            /**
             * Body on the form's own page, markdown. Replaces the page's default copy entirely, so
             * only text authored here counts as an override.
             */
            pageText?: string;
            /**
             * @deprecated Replaced with `cardText` (home page card) and `pageText` (the form's own
             * page). This single field fed both before they were split. No longer written — kept
             * because it is still read as the card text's fallback, so wording entered before the
             * split survives until the operator saves the form once.
             */
            description?: string;
            imageRef?: string;
            /**
             * Whether the form is offered at all: false hides its card on the home page and makes
             * its own page 404. The configuration is kept either way, so switching it back on
             * restores the page with its copy intact. Defaults to true when unset.
             */
            isActive?: boolean;
            /**
             * Whether the card is listed on the home page. Narrower than `isActive`: the form's own
             * page stays reachable through its link. Defaults to true when unset.
             */
            isVisible?: boolean;
        };
        reservation?: {
            emailTo: string;
            serviceEmail?: string;
            servicePhone?: string;
            useUnstyledEmail?: boolean;
            /** Card headline on the home page. Falls back to the translated default when unset. */
            cardTitle?: string;
            /** Card body on the home page. Plain text, no formatting. */
            cardText?: string;
            /** Label of the card's button. Falls back to the translated default when unset. */
            cardButtonText?: string;
            /** Headline on the form's own page. Falls back to the translated default when unset. */
            pageTitle?: string;
            /**
             * Body on the form's own page, markdown. Replaces the page's default copy entirely, so
             * only text authored here counts as an override.
             */
            pageText?: string;
            /**
             * @deprecated Replaced with `cardText` (home page card) and `pageText` (the form's own
             * page). This single field fed both before they were split. No longer written — kept
             * because it is still read as the card text's fallback, so wording entered before the
             * split survives until the operator saves the form once.
             */
            description?: string;
            imageRef?: string;
            /**
             * Whether the form is offered at all: false hides its card on the home page and makes
             * its own page 404. The configuration is kept either way, so switching it back on
             * restores the page with its copy intact. Defaults to true when unset.
             */
            isActive?: boolean;
            /**
             * Whether the card is listed on the home page. Narrower than `isActive`: the form's own
             * page stays reachable through its link. Defaults to true when unset.
             */
            isVisible?: boolean;
        };
    };
    gretaIconVariant?: GretaIconVariant;
    metadata?: WebsiteMetadataConfiguration;
    partnerLinksDropdown?: {
        isVisible?: boolean;
        excludedCinemaIds?: CinemaDto['id'][];
        cinemaChainUrl?: string;
        cinemaChainLabel?: string;
        additionalLinks?: Array<{
            label: string;
            url: string;
        }>;
    };
    /**
     * Optional second logo shown next to the cinema's own logo in the website
     * header (e.g. the venue building's owner or a partner brand). Unset hides it.
     */
    secondaryLogo?: {
        url: string;
        href?: string;
    };
    /**
     * Custom text shown in the modal that opens when a visitor taps the
     * ticket button for a showing without an online ticket URL. Per-language,
     * rendered with `getTranslationWithFallback`; falls back to the default
     * translation (`program.no-online-ticketing-link.text`) when unset.
     */
    emptyTicketLinkText?: Partial<Record<ISO_639_3_Locale, string>>;
    /**
     * Controls whether links to other domains (cinema links, footer links,
     * partner links, shop links, social media icons, description links, …)
     * open in the same tab or a new tab. Unset behaves like `new-tab`, which
     * is how the website has always opened external links.
     */
    externalLinksTarget?: 'same-tab' | 'new-tab';
    /**
     * Whether this cinema has access to Movie Matching in the cinema app — the nav entry under
     * Program and the `/[cinemaId]/movie-matching` page itself. Granted by Cineamo staff in the
     * admin app's cinema settings, not by the operator. Unset means no access.
     *
     * The only key here that is not about the cinema website: it lives in this blob because that
     * is where per-cinema configuration without its own `cinema_configuration` column goes, and
     * the flag needs no schema change to be rolled out or revoked per cinema.
     */
    isMovieMatchingEnabled?: boolean;
};
